// Record Delivery (mock A3) — the presentational blocks of the drawer body. State lives in ../DeliveryPanel.jsx.
import { targetDate } from '../dispatchEta.mjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQtySanitizer } from '../../../hooks/useItemQtyDecimals';   // C6: quantity typing stops at the item's qty_decimals
import { Emo } from '../../../shell/EmojiIcons';
import { formatDate, formatQty, formatExact } from '../../../utils/format';
import { qtyText, baseUnit, unitGroups, groupsText } from '../dispatchUnits.mjs';
import { lineCap, slipLineIssue, slipLineShort, slipAllZero, isZeroLine, unitOf, freeConv, entryStamp } from './deliveryModel.mjs';
import { TextInput } from '../../../components/inputs/Input';
import EnumSelect from '../../../components/EnumSelect';

export const fq = (q, l) => qtyText(q, l, formatQty);
const sumText = entries => groupsText(unitGroups(entries), formatQty);
export const slipText = s => sumText((s.lines || []).map(sl => ({ qty: Number(sl.quantity), line: sl.line })));

// ordered / delivered / open-dispatched / available to free-deliver, one strip per open line
export function QtyContext({ lines }) {
  const { t } = useTranslation('delivery');
  return lines.map(l => (
    <div className="rd-qtyline" key={l.sale_id} data-testid={'rd-ctx-' + l.line_id}>
      <span className="it">{l.item_name || '—'}:</span>
      <span>{t('ctx.ordered')} <b>{fq(l.qty, l)}</b></span>/<span>{t('ctx.delivered')} <b>{fq(l.qty_delivered, l)}</b></span>/
      <span>{t('ctx.openDispatched')} <b>{fq(l.qty_open_dispatched, l)}</b></span>/<span>{t('ctx.available')} <b>{fq(lineCap(l), l)}</b></span>
    </div>
  ));
}

// Open dispatches (n) + the Direct delivery (no slip) radio
export function SlipPick({ slips, mode, onPick, freeCapText }) {
  const { t } = useTranslation('delivery');
  return (
    <div>
      <div className="sec-lab">{t('pick.title', { n: slips.length })}</div>
      <div className="rd-radios" role="radiogroup" data-testid="delivery-pick">
        {slips.map(s => (
          <label key={s.slip_id} className={'rd-radio' + (mode === s.slip_id ? ' on' : '')}>
            <input type="radio" name="rd-mode" checked={mode === s.slip_id} onChange={() => onPick(s.slip_id)} />
            <div>
              <div className="rt mono">{s.dispatch_no}</div>
              <div className="rs">{[s.delivery_method === 'pickup' ? t('pick.pickup') : null, t('pick.eta', { date: formatDate(targetDate(s)) }), t('pick.dispatched', { date: formatDate(s.dispatch_date) }), slipText(s), s.vehicle, s.driver].filter(Boolean).join(' · ')} · <span className="rd-chip">{t('pick.inTransit')}</span></div>
            </div>
          </label>
        ))}
        {slips.length === 0 && <div className="sub" style={{ fontSize: 12, color: 'var(--text-3)' }}>{t('pick.none')}</div>}
        <label className={'rd-radio' + (mode === 'free' ? ' on' : '')}>
          <input type="radio" name="rd-mode" checked={mode === 'free'} onChange={() => onPick('free')} />
          <div><div className="rt">{t('pick.direct')}</div><div className="rs">{t('pick.directHint', { cap: freeCapText })}</div></div>
        </label>
      </div>
    </div>
  );
}

// "Delivering BL-… (qty)" — every slip line, editable down only; inline refusals + the short-delivery warning
export function SlipBox({ slip, qtys, onQty }) {
  const { t } = useTranslation('delivery'); const qs = useQtySanitizer();
  const multi = slip.lines.length > 1;
  const issues = slip.lines.map(sl => slipLineIssue(sl, qtys[sl.id], multi));
  const allZero = slipAllZero(slip, qtys);
  const zeros = slip.lines.filter(sl => isZeroLine(qtys[sl.id], multi));
  const short = slip.lines.map(sl => ({ qty: slipLineShort(sl, qtys[sl.id]), line: sl.line })).filter(e => e.qty > 0);
  const got = slip.lines.map(sl => ({ qty: Math.min(Number(qtys[sl.id]) || 0, Number(sl.quantity)), line: sl.line }));
  return (
    <div className="rd-edit" data-testid="delivery-slip-lines">
      <div className="ttl">{t('slip.delivering')} <span className="mono">{slip.dispatch_no}</span> ({slipText(slip)})</div>
      <div className="rd-nd" data-testid="delivery-slip-dates">{[t('pick.eta', { date: formatDate(targetDate(slip)) }), t('pick.dispatched', { date: formatDate(slip.dispatch_date) })].filter(Boolean).join(' · ')}</div>
      {slip.lines.map((sl, i) => {
        const issue = issues[i];
        return (
          <div className="rd-row" key={sl.id}>
            {multi && <div className="nm">{sl.line.item_name || '—'}</div>}
            <div className="rd-fld">
              <TextInput type="number" min="0" step="any" value={qtys[sl.id] ?? ''} className={issue ? 'bad' : undefined} aria-invalid={!!issue} data-testid={'dlv-slip-qty-' + sl.sales_id}
                aria-label={t('slip.qtyFor', { item: sl.line.item_name })} onChange={e => onQty(sl.id, qs(e.target.value, sl.line.item_id))} />
              <span className="echo">{baseUnit(sl.line) ? baseUnit(sl.line) + ' — ' : ''}{t('slip.prefilled')}</span>
            </div>
            {issue === 'exceeds' && <div className="ferr" role="alert"><Emo name="stop" /> {t('slip.exceeds', { qty: fq(sl.quantity, sl.line) })}</div>}
            {issue === 'positive' && <div className="ferr" role="alert"><Emo name="stop" /> {t(multi ? 'slip.positiveMulti' : 'slip.positive', { item: sl.line.item_name })}</div>}
            {isZeroLine(qtys[sl.id], multi) && <div className="rd-nd" data-testid={'dlv-notdelivered-' + sl.sales_id}><b>{t('slip.notDelivered')}</b> · {t('slip.staysOpen')}</div>}
          </div>
        );
      })}
      {allZero && <div className="ferr" role="alert" data-testid="delivery-allzero"><Emo name="stop" /> {t('slip.allZero')}</div>}
      {short.length > 0 && !issues.some(Boolean) && !allZero && (
        <div className="rd-warnbox" data-testid="delivery-shortfall"><Emo name="warn" /> {t('slip.short', { short: sumText(short), got: sumText(got), sent: slipText(slip) })}
          {zeros.length > 0 && <div style={{ marginTop: 4 }}>{t('slip.notDeliveredSummary', { items: zeros.map(sl => sl.line.item_name || '—').join(', ') })}</div>}</div>
      )}
    </div>
  );
}

// Direct delivery (no slip) — free entry per open line, capped at what no open slip reserves.
// 47080 (R5b): a line sold in a sale unit is typed in that unit by default, with a unit picker (sale | item unit), the live conversion,
// the max in the picked unit and a warning when the quantity is not a whole number of the sale unit.
export function FreeBox({ lines, qtys, onQty, issueOf, reservedBy, extra, units, onUnit, dpOf }) {
  const { t } = useTranslation('delivery');
  return (
    <div className="rd-edit" data-testid="delivery-free-lines">
      <div className="ttl">{t('free.title')}</div>
      {lines.map(l => <FreeLine key={l.sale_id} l={l} multi={lines.length > 1} raw={qtys[l.sale_id]} unit={unitOf(l, units)} dp={dpOf(l)} issue={issueOf(l)} by={reservedBy(l)}
        onQty={v => onQty(l.sale_id, v)} onUnit={u => onUnit(l.sale_id, u)} extra={extra} />)}
    </div>
  );
}

function FreeLine({ l, multi, raw, unit, dp, issue, by, onQty, onUnit, extra }) {
  const { t } = useTranslation('delivery'); const qs = useQtySanitizer();
  const cap = lineCap(l), su = entryStamp(l), bu = baseUnit(l), sale = unit === 'sale';
  const c = freeConv(l, raw, unit, dp), capS = sale ? freeConv(l, String(cap), 'base', null) : null;
  const maxTxt = !su ? fq(cap, l) : capS ? `${formatExact(capS.sale)} ${su.label} (${formatQty(cap)} ${bu})` : `${formatQty(cap)} ${bu}`;
  const vars = c && su ? { base: formatExact(c.base), baseUnit: bu, sale: formatExact(c.sale), saleUnit: su.label, ratio: formatExact(String(l.sale_unit_ratio)), dp: dp ?? 2 } : null;
  return (
    <div className="rd-row">
      {multi && <div className="nm">{l.item_name || '—'}</div>}
      <div className="rd-fld">
        <TextInput type="number" min="0" step="any" value={raw ?? ''} placeholder="0" className={issue ? 'bad' : undefined} aria-invalid={!!issue} data-testid={'dlv-free-qty-' + l.sale_id}
          aria-label={t('free.qtyFor', { item: l.item_name })} onChange={e => onQty(sale ? e.target.value : qs(e.target.value, l.item_id))} />
        {su ? <EnumSelect bare testid={'dlv-free-unit-' + l.sale_id} value={unit} onChange={onUnit} title={t('free.unitFor', { item: l.item_name })} ariaLabel={t('free.unitFor', { item: l.item_name })} wrapStyle={{ flex: '0 0 auto', width: 110 }}>
          <option value="sale">{su.label}</option><option value="base">{bu || t('free.baseUnit')}</option></EnumSelect> : null}
        <span className="echo">{vars && !issue && <b data-testid={'dlv-conv-' + l.sale_id}>{sale ? t('free.convSale', vars) : t('free.convBase', vars)} · </b>}{!su && bu ? bu + ' · ' : ''}{t('free.max')} <b>{maxTxt}</b></span>
      </div>
      {vars && !issue && !c.wholeSale && <div className="rd-warnbox" role="note" data-testid={'dlv-notwhole-' + l.sale_id}><Emo name="warn" /> {t('free.notWhole', vars)}</div>}
      {issue === 'grid' && vars && <div className="ferr" role="alert" data-testid={'dlv-grid-' + l.sale_id}><Emo name="stop" /> {t('free.offGrid', vars)} <code>DELIVERY_SALE_QTY_OFF_GRID</code></div>}
      {issue === 'exceeds' && (
        <div className="ferr" role="alert" data-testid={'dlv-over-' + l.sale_id}><Emo name="stop" /> {Number(l.qty_open_dispatched) > 0
          ? <>{t('free.reserved', { cap: fq(cap, l), open: fq(l.qty_open_dispatched, l), by: by.join(', ') })} <code>QTY_RESERVED_BY_DISPATCH</code></>
          : t('free.over', { cap: fq(cap, l) })}</div>
      )}
      {issue === 'positive' && <div className="ferr" role="alert"><Emo name="stop" /> {t('free.positive', { item: l.item_name })}</div>}
      {extra && extra(l)}
    </div>
  );
}

// Already delivered: date · qty · BL (or 'no slip')
export function PriorDeliveries({ rows }) {
  const { t } = useTranslation('delivery');
  return (
    <div>
      <div className="sec-lab">{t('prior.title')}</div>
      <div className="gtable-wrap" data-testid="delivery-prior"><table className="gtable zebra"><tbody>
        {rows.length === 0 ? <tr><td className="sub">—</td></tr> : rows.map(r => (
          <tr key={r.key}><td className="l">{r.date ? formatDate(String(r.date).slice(0, 10)) : '—'}</td>
            <td className="r num">{sumText(r.entries)}</td>
            <td className="mono">{r.bl || <span className="sub">{t('prior.noSlip')}</span>}</td></tr>
        ))}
      </tbody></table></div>
    </div>
  );
}

// Soft date notes under the delivery date (never blocking): amber for before-dispatch / future, plain hint for after-ETA / after-expected.
export function DateNotes({ notes }) {
  const { t } = useTranslation('delivery');
  return notes.map(n => {
    const txt = t('dates.' + n.kind, { count: n.count, date: formatDate(n.date), item: n.item });
    return n.tone === 'warn'
      ? <div className="rd-warnbox" key={n.kind} data-testid={'delivery-date-' + n.kind}><Emo name="warn" /> {txt}</div>
      : <div className="hint" key={n.kind + (n.item || '')} data-testid={'delivery-date-' + n.kind}>{txt}</div>;
  });
}
